// Technique glyphs for the 주법 palette. Each is a 24×24 stroke icon (currentColor),
// matching the rest of the Tutti icon set. Visual language:
//   bends = vertical arrows · slides = diagonal arrows (dot marks the note) ·
//   hammer/pull & tie = slur arcs (tie's arc sits below) · vibrato/trill/tremolo = waves ·
//   harmonics = diamonds · the conventional text glyphs (P.M./L.R.) stay as letters.
import type { JSX } from "react";   // React 19: the JSX namespace is no longer global

const Dot = ({ x, y, r = 2.1 }: { x: number; y: number; r?: number }) => (
  <circle cx={x} cy={y} r={r} fill="currentColor" stroke="none" />
);

// vertical arrow: line from yBot up to yTop with an up-chevron (dir=-1) or down-chevron (dir=1)
const VArrow = ({ x, yTop, yBot, dir = -1 }: { x: number; yTop: number; yBot: number; dir?: 1 | -1 }) => {
  const head = dir === -1 ? `M${x - 3} ${yTop + 4} L${x} ${yTop} L${x + 3} ${yTop + 4}` : `M${x - 3} ${yBot - 4} L${x} ${yBot} L${x + 3} ${yBot - 4}`;
  return (<><path d={`M${x} ${yBot} L${x} ${yTop}`} /><path d={head} /></>);
};

function body(fx: string): JSX.Element | null {
  switch (fx) {
    // ---- bends: up arrow length encodes the amount (full / half / quarter); release = up+down ----
    case "b": return <VArrow x={12} yTop={4} yBot={20} />;
    case "bh": return <VArrow x={12} yTop={10} yBot={20} />;
    case "bq": return <VArrow x={12} yTop={14} yBot={20} />;
    case "br": return <><VArrow x={8} yTop={6} yBot={19} /><VArrow x={16} yTop={6} yBot={19} dir={1} /></>;

    // ---- hammer-on/pull-off: slur arc over two notes ----
    case "h": return <><Dot x={6} y={17} /><Dot x={18} y={17} /><path d="M6 15 Q12 5 18 15" /></>;
    // ---- tie (이음줄): two notes joined by a tie arc below ----
    case "tie": return <><Dot x={6} y={9} /><Dot x={18} y={9} /><path d="M6 11 Q12 21 18 11" /></>;
    // ---- grace notes (꾸밈음): a small ornament note leading into the main note ----
    case "grh": return <><Dot x={6} y={16} r={1.5} /><path d="M6 14 Q12 4 18 13" /><Dot x={18} y={15} r={2.5} /></>;   // hammer slur
    case "grsl": return <><Dot x={5} y={17} r={1.5} /><path d="M7 15 L16 8" /><path d="M16 8 l-3.6 0.4 M16 8 l-0.4 3.6" /><Dot x={18} y={6} r={2.5} /></>;  // slide line

    // ---- slides: diagonal; dot marks the sounded note (in → dot at destination, out → dot at origin) ----
    case "sl": return <><path d="M6 18 L18 6" /><path d="M18 6 l-4 0.6 M18 6 l-0.6 4" /></>;            // slide
    case "ss": return <><path d="M6 18 L18 6" /><path d="M18 6 l-4 0.6 M18 6 l-0.6 4" /><path d="M6 11 Q12 6 18 11" /></>; // legato/shift slide (slur over)
    case "sib": return <><Dot x={18} y={6} /><path d="M5 19 L14 10" /><path d="M14 10 l3.4 -0.5 M14 10 l-0.5 3.4" /></>;  // slide in ↗
    case "sia": return <><Dot x={18} y={18} /><path d="M5 5 L14 14" /><path d="M14 14 l3.4 0.5 M14 14 l-0.5 -3.4" /></>;  // slide in ↘
    case "sou": return <><Dot x={6} y={18} /><path d="M9 15 L19 5" /><path d="M19 5 l-4 0.6 M19 5 l-0.6 4" /></>;        // slide out ↗
    case "sod": return <><Dot x={6} y={6} /><path d="M9 9 L19 19" /><path d="M19 19 l-4 -0.6 M19 19 l0.6 -4" /></>;       // slide out ↘

    // ---- vibrato / trill / tremolo: waves ----
    case "v": return <path d="M3 12 q3 -4.5 6 0 t6 0 t6 0" />;
    case "vw": return <path d="M3 12 q3 -7 6 0 t6 0 t6 0" />;
    case "tr": return <path d="M4 16 L8 8 L12 16 L16 8 L20 16" />;
    case "tp": return <><path d="M7 17 L12 7" /><path d="M11 17 L16 7" /><path d="M15 17 L20 7" /></>;
    case "tt": return <><path d="M6 7 H18" /><path d="M12 7 V18" /></>;   // T = tapping

    // ---- nuance ----
    case "pm": return <text x="12" y="15.5" textAnchor="middle" fontSize="8.5" fontWeight="700" fill="currentColor" stroke="none" fontFamily="var(--font)">P.M</text>;
    case "lr": return <text x="12" y="15.5" textAnchor="middle" fontSize="8.5" fontWeight="700" fill="currentColor" stroke="none" fontFamily="var(--font)">L.R</text>;
    case "st": return <Dot x={12} y={12} r={2.8} />;                     // staccato dot
    case "ac": return <path d="M7 6 L16 12 L7 18" />;                    // accent >
    case "x": return <><path d="M7 7 L17 17" /><path d="M17 7 L7 17" /></>;  // dead note

    // ---- harmonics: diamond (natural outline · artificial +dot · pinch filled) ----
    case "nh": return <path d="M12 5 L19 12 L12 19 L5 12 Z" />;
    case "ah": return <><path d="M12 5 L19 12 L12 19 L5 12 Z" /><Dot x={12} y={12} r={1.8} /></>;
    case "ph": return <path d="M12 5 L19 12 L12 19 L5 12 Z" fill="currentColor" />;

    default: return null;
  }
}

export function TechIcon({ fx, size = 22 }: { fx: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {body(fx)}
    </svg>
  );
}
